import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  Stack,
  Typography,
  useTheme,
} from "@mui/material"
import React from "react"
import CloseIcon from "@mui/icons-material/Close"
import { useTranslation } from "react-i18next"
import { styles } from "./styles/ShareNotes.styles"
import TextInput from "components/UI/TextInput"
import useApi from "modules/studentNotes"
import DeleteIcon from "@mui/icons-material/Delete"
import { useAuthContext } from "contexts/AuthContext"
import { useGlobalAlert } from "contexts/GlobalAlertContext"

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

export default function ShareNotes({
  open,
  onClose,
  fontSettings,
  title,
  noteId,
}) {
  const { t } = useTranslation()
  const [username, setUsername] = React.useState("")
  const [selectedIndex, setSelectedIndex] = React.useState(0)
  const [linkedUsers, setLinkedUsers] = React.useState([])
  const { getUsersLinked, deleteLink, updateLink, addLink } = useApi()
  const { selectedGroupData } = useAuthContext()
  const { showAlert } = useGlobalAlert()
  const theme = useTheme()

  React.useEffect(() => {
    if (open && noteId !== undefined) {
      setUsername("")
      getUsersLinked(noteId).then((data) => {
        setLinkedUsers(data)
      })
    }
    return () => {
      setLinkedUsers([])
      setUsername("")
    }
  }, [open, noteId])

  const deleteUserLink = async (index) => {
    const userId = linkedUsers[index].userId
    const response = await deleteLink(noteId, userId)
    if (response) {
      const newLinkedUsers = [...linkedUsers]
      newLinkedUsers.splice(index, 1)
      setLinkedUsers(newLinkedUsers)
    }
  }

  const updateUserLink = async (index, canMakeChanges) => {
    const userId = linkedUsers[index].userId
    const response = await updateLink(noteId, userId, canMakeChanges)
    if (response) {
      const newLinkedUsers = [...linkedUsers]
      newLinkedUsers[index].AllowUpdate = canMakeChanges
      setLinkedUsers(newLinkedUsers)
    }
  }

  const share = async () => {
    const teacherImage = selectedGroupData?.teachers?.[0]?.avatar
    if (username.trim() === "") {
      showAlert(t("enter_username"), teacherImage)
      return
    }
    const response = await addLink({
      noteId: noteId,
      username,
      allowEdit: selectedIndex === 0,
    })
    if (response) {
      showAlert(t("saved"), teacherImage)
      const newLinkedUsers = [...linkedUsers]
      newLinkedUsers.push(response)
      setLinkedUsers(newLinkedUsers)
      setUsername("")
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      fullWidth
      PaperProps={{
        sx: [
          styles.paper,
          fontSettings?.fontFamily && {
            "& .MuiTypography-root, button, textarea, input, .ql-editor p": {
              fontFamily: fontSettings.fontFamily,
            },
          },
          fontSettings?.bgColor && {
            backgroundColor: fontSettings.bgColor,
          },
        ],
      }}
    >
      <IconButton aria-label="close" onClick={onClose} sx={styles.closeButton}>
        <CloseIcon fontSize="medium" />
      </IconButton>
      <DialogTitle textAlign={"center"} fontSize={15} sx={styles.divider}>
        {title}
      </DialogTitle>
      <DialogContent>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={"flex-start"}
        >
          <Typography variant="span" fontSize={14}>
            {t(`share_this_note_with`)}
          </Typography>
          <TextInput
            fullWidth
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            InputProps={{
              sx: styles.input,
            }}
            placeholder={t("enter_username")}
          />
        </Stack>
        <Stack
          flexDirection={"row"}
          alignItems={"center"}
          justifyContent={"center"}
          spacing={1}
        >
          <Typography variant="span" fontSize={14}>
            {t(`can_make_changes`)}
          </Typography>
          <Button
            variant="contained"
            disableElevation
            disableRipple
            disableFocusRipple
            sx={[styles.yesNoButton, selectedIndex === 0 && styles.yesButton]}
            onClick={() => {
              setSelectedIndex(0)
            }}
          >
            {t("yes")}
          </Button>
          <Button
            variant="contained"
            disableElevation
            disableRipple
            disableFocusRipple
            onClick={() => {
              setSelectedIndex(1)
            }}
            sx={[styles.yesNoButton, selectedIndex === 1 && styles.noButton]}
          >
            {t("no")}
          </Button>
        </Stack>
        <Stack>
          <Button
            variant="contained"
            color="success"
            disableElevation
            onClick={share}
            sx={[
              styles.button,
              {
                alignSelf: "flex-end",
              },
            ]}
          >
            {t("add")}
          </Button>
        </Stack>
        <Stack>
          <Typography sx={styles.shareWith}>{"Share with"}</Typography>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={"space-between"}
          >
            <Box flex={1}>
              <Typography textAlign={"center"}>{t("full_name")}</Typography>
            </Box>
            <Box flex={1}>
              <Typography textAlign={"center"}>{t("username")}</Typography>
            </Box>
            <Box flex={1}>
              <Typography textAlign={"center"}>
                {t("can_make_changes")}
              </Typography>
            </Box>
            <Box flex={1 / 4}></Box>
          </Stack>
          {linkedUsers.map((item, index) => {
            return (
              <Stack
                key={index}
                direction="row"
                alignItems="center"
                justifyContent={"space-between"}
              >
                <Box flex={1}>
                  <Typography
                    textAlign={"center"}
                  >{`${item.name} ${item.lastName}`}</Typography>
                </Box>
                <Box flex={1}>
                  <Typography
                    textAlign={"center"}
                  >{`${item.username}`}</Typography>
                </Box>
                <Stack
                  flex={1}
                  flexDirection={"row"}
                  alignItems={"center"}
                  justifyContent={"center"}
                >
                  <Button
                    variant="contained"
                    disableElevation
                    disableRipple
                    disableFocusRipple
                    sx={[
                      styles.yesNoButton,
                      item.AllowUpdate && styles.yesButton,
                      {
                        padding: "4px 4px",
                        borderRadius: "5px",
                        fontSize: "12px",
                        minWidth: 0,
                      },
                    ]}
                    onClick={() => updateUserLink(index, true)}
                  >
                    {t("yes")}
                  </Button>
                  <Button
                    variant="contained"
                    disableElevation
                    disableRipple
                    disableFocusRipple
                    sx={[
                      styles.yesNoButton,
                      !item.AllowUpdate && styles.noButton,
                      {
                        padding: "4px 4px",
                        borderRadius: "5px",
                        fontSize: "12px",
                        minWidth: 0,
                      },
                    ]}
                    onClick={() => updateUserLink(index, false)}
                  >
                    {t("no")}
                  </Button>
                </Stack>
                <Box flex={1 / 4}>
                  <IconButton onClick={() => deleteUserLink(index)}>
                    <DeleteIcon color={theme.palette.common.black} />
                  </IconButton>
                </Box>
              </Stack>
            )
          })}
        </Stack>
      </DialogContent>
    </Dialog>
  )
}
